import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid2,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Tour from "../TourInterface.ts";
import Button from "../../basics/Button.tsx";
import PhoneIcon from '@mui/icons-material/Phone';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import CelebrationIcon from '@mui/icons-material/Celebration';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AxiosInstance from "../../../utils/Axios.ts";

interface TourOverlayProps {
  tour: Tour;
  onClose: () => void;
}

const TourOverlay: React.FC<TourOverlayProps> = ({ tour, onClose }) => {
  const [formData, setFormData] = useState({
    child_num: tour.child_num,
    infant_num: tour.infant_num,
    escort: tour.escort,
    final_pax: tour.final_pax,
    supplier_called: tour.supplier_called,
    actual_departure_time: tour.actual_departure_time,
    actual_arrival_time: tour.actual_arrival_time,
    comment: tour.comment,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSave = async () => {
    try {
      const response = await AxiosInstance.patch(`/tour/patch/${tour.id}/`, formData);
      console.log("Tour updated:", response.data);
      onClose();
    } catch (err) {
      console.error("Failed to update tour.", err);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth fullScreen={true}>
      <DialogTitle justifyItems={"center"} width={"100%"}>
        <Grid2 container spacing={2} width={"100%"} paddingTop={2}>
          <Grid2 size={3}>
            <Typography textAlign={"left"} fontSize={14} noWrap>
              Sign: {tour.bus_sign}
            </Typography>
          </Grid2>
          <Grid2 size={6}>
            <Typography textAlign="center" fontSize={16} noWrap>
            <b>{tour.name} - {tour.tour_code}</b>
            </Typography>
          </Grid2>
          <Grid2 size={3}>
            <Typography textAlign="right" fontSize={14} noWrap>
              Park:{tour.park_spot}
            </Typography>
            </Grid2>
        </Grid2>
      </DialogTitle>
      <DialogContent dividers>
        <Grid2 container spacing={2}>
          <Grid2 container spacing={2}>
            <Grid2 size={4}>
              <Typography textAlign="center" fontSize={12}>
                Min: {tour.min_size}
              </Typography>
            </Grid2>
            <Grid2 size={4}>
              <Typography textAlign="center" fontSize={12}>
                #: {tour.size}
              </Typography>
            </Grid2>
            <Grid2 size={4}>
              <Typography textAlign="center" fontSize={12}>
                Max: {tour.max_size}
              </Typography>
            </Grid2>
            <Grid2 size={3}>
              <TextField
                fullWidth
                label="Child"
                type="number"
                name="child_num"
                value={formData.child_num}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={3}>
              <TextField
                fullWidth
                label="Infant"
                type="number"
                name="infant_num"
                value={formData.infant_num}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={3}>
              <TextField
                fullWidth
                label="Escort"
                type="number"
                name="escort"
                value={formData.escort}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={3}>
              <TextField
                fullWidth
                label="Final"
                type="number"
                name="final_pax"
                value={formData.final_pax}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.supplier_called}
                    onChange={handleSwitchChange}
                    name="supplier_called"
                  />
                }
                label="Supplier Called"
              />
            </Grid2>
          </Grid2>
          <Divider sx={{width:"100%", borderRadius: 1}}/>
          <Grid2 container spacing={2} >
            <Grid2 size={4}>
              <Typography textAlign="center" fontSize={11}>
                Check In: {tour.check_in_time}
              </Typography>
            </Grid2>
            <Grid2 size={4}>
              <Typography textAlign="center" fontSize={11}>
                Departure: {tour.time_from}
              </Typography>
            </Grid2>
            <Grid2 size={4}>
              <Typography textAlign="center" fontSize={11}>
                Arrival: {tour.time_to}
              </Typography>
            </Grid2>
            <Grid2 size={6}>
              <TextField
                fullWidth
                label="Actual Departure"
                type="time"
                name="actual_departure_time"
                value={formData.actual_departure_time}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                fullWidth
                label="Actual Arrival"
                type="time"
                name="actual_arrival_time"
                value={formData.actual_arrival_time}
                onChange={handleChange}
              />
            </Grid2>
          </Grid2>
          <Divider sx={{width:"100%", borderRadius: 1}}/>
          <Grid2 container spacing={2} width={"100%"} >
            <Grid2 container spacing={2} alignItems={"center"} justifyContent={"center"} border={1} borderRadius={2} width={"100%"} >
              <Grid2 size={2} marginTop={1}>
                <Typography textAlign="center">
                  <DirectionsBusIcon />
                </Typography>
              </Grid2>
              <Grid2 size={6} alignItems={"center"} justifyContent={"center"}>
                <Typography textAlign="center">
                  {tour.bus_name} - {tour.bus_company}
                </Typography>
              </Grid2>
              <Grid2 size={4} alignItems={"center"} justifyContent={"center"}>
                <Typography textAlign="center">
                  size: {tour.bus_size ?? "—"}
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={2} alignItems={"center"} justifyContent={"center"} border={1} borderRadius={2} width={"100%"} >
              <Grid2 size={2} marginTop={1}>
                <Typography textAlign="center">
                  <AirlineSeatReclineNormalIcon />
                </Typography>
              </Grid2>
              <Grid2 size={5} alignItems={"center"} justifyContent={"center"}>
                <Typography textAlign="center">
                  {tour.driver}
                </Typography>
              </Grid2>
              <Grid2 size={5} alignItems={"center"} justifyContent={"center"}>
                <Typography textAlign="center">
                  <PhoneIcon style={{ marginRight: "5px" }} />: {tour.driver_phone}
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={2} alignItems={"center"} justifyContent={"center"} border={1} borderRadius={2} width={"100%"} >
              <Grid2 size={2} marginTop={1}>
                <Typography textAlign="center">
                  <EmojiPeopleIcon />
                </Typography>
              </Grid2>
              <Grid2 size={6} alignItems={"center"} justifyContent={"center"}>
                <Typography textAlign="center">
                  {tour.guide_name}
                </Typography>
              </Grid2>
              <Grid2 size={4} alignItems={"center"} justifyContent={"center"}>
                <Typography textAlign="center">
                  <PhoneIcon style={{ marginRight: "5px" }} />: {tour.guide_phone}
                </Typography>
              </Grid2>
            </Grid2>
          </Grid2>
          <Divider sx={{width:"100%", borderRadius: 1}}/>
          <Grid2 container spacing={2} >
          {tour.meal && (
            <Grid2 size={12}>
              <Typography sx={{ fontSize: 14, mt: "2%", textAlign: "center" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
              <RestaurantMenuIcon style={{ marginRight: "5px" }} />: {tour.meal}
              </Typography>
            </Grid2>
          )}
          {tour.activity_1 && (
            <Grid2 size={6}>
              <Typography sx={{ mt: "2%", textAlign: "center" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
              <CelebrationIcon style={{ marginRight: "5px" }} />: {tour.activity_1_name}
              </Typography>
            </Grid2>
            )}
            {tour.activity_1 && (
            <Grid2 size={6}>
              <Typography sx={{ fontSize: 14, mt: "2%", textAlign: "center" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
              <AccessTimeIcon style={{ marginRight: "5px" }} />: {tour.activity_1_time}
              </Typography>
            </Grid2>
            )}
            {tour.activity_1 && (
            <Grid2 size={12}>
              <Typography sx={{ fontSize: 14, mt: "2%", textAlign: "center" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
              <PhoneIcon style={{ marginRight: "5px" }} />: {tour.activity_1_phone}
              </Typography>
            </Grid2>
          )}
          {tour.activity_2 && (
            <Grid2 size={6}>
              <Typography sx={{ mt: "2%", textAlign: "center" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
              <CelebrationIcon style={{ marginRight: "5px" }} />: {tour.activity_2_name}
              </Typography>
            </Grid2>
            )}
            {tour.activity_2 && (
            <Grid2 size={6}>
              <Typography sx={{ fontSize: 14, mt: "2%", textAlign: "center" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
              <AccessTimeIcon style={{ marginRight: "5px" }} />: {tour.activity_2_time}
              </Typography>
            </Grid2>
            )}
            {tour.activity_2 && (
            <Grid2 size={12}>
              <Typography sx={{ fontSize: 14, mt: "2%", textAlign: "center" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
              <PhoneIcon style={{ marginRight: "5px" }} />: {tour.activity_2_phone}
              </Typography>
            </Grid2>
          )}

          </Grid2>
          <Divider sx={{width:"100%", borderRadius: 1}}/>
          <Grid2 size={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comment"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
            />
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button text={"Save"} onClick={handleSave} type="button" color="primary"/>
      </DialogActions>
    </Dialog>
  );
};

export default TourOverlay;
